import React, { useEffect, useRef, useState } from "react";
import Select from "react-select";
import DatePicker from "react-multi-date-picker";
import { useBulkExportReport } from "../../../backend/exports";
import { toast } from "react-toastify";
import PageLoader from "../../shared/PageLoader";
import '../Vendor/TaskManagement/VendorAuditBulkDownloadModal.css';

const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
];

interface Props {
    onClose: () => void;
    parentCompanies: any[];
    initialCompany?: { value: any; label: string } | null;
    initialAssociateCompany?: { value: any; label: string } | null;
    initialLocation?: { value: any; label: string } | null;
    initialCategory?: { value: any; label: string } | null;
    initialVendor?: { value: any; label: string } | null;
    initialAssociateCompanies?: any[];
    initialLocations?: any[];
    initialCategories?: any[];
    initialVendors?: any[];
}

function VendorAuditDashboardBulkModal({
    onClose,
    parentCompanies,
    initialCompany,
    initialAssociateCompany,
    initialLocation,
    initialCategory,
    initialVendor,
    initialAssociateCompanies,
    initialLocations,
    initialCategories,
    initialVendors,
}: Props) {
    const [isOpen, setIsOpen] = useState(false);
    const [company, setCompany] = useState<any>(initialCompany || null);
    const [associateCompany, setAssociateCompany] = useState<any>(initialAssociateCompany || null);
    const [location, setLocation] = useState<any>(initialLocation ? [initialLocation] : []);
    const [category, setCategory] = useState<any>(initialCategory ? [initialCategory] : []);
    const [vendor, setVendor] = useState<any>(initialVendor ? [initialVendor] : []);
    const [associateCompanies, setAssociateCompanies] = useState<any[]>(initialAssociateCompanies || []);
    const [locations, setLocations] = useState<any[]>(initialLocations || []);
    const [pickerRange, setPickerRange] = useState<any[]>([]);
    const [monthYears, setMonthYears] = useState<string[]>([]);

    useEffect(() => {
        requestAnimationFrame(() => setIsOpen(true));
    }, []);

    useEffect(() => {
        if (initialCompany && parentCompanies) {
            const _company = parentCompanies.find((c: any) => c.id == initialCompany.value);
            if (_company) setAssociateCompanies(_company.associate_companies || []);
        }
    }, [parentCompanies]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (initialAssociateCompany && parentCompanies && initialCompany) {
            const _company = parentCompanies.find((c: any) => c.id == initialCompany.value);
            const _ac = _company && _company.associate_companies
                ? _company.associate_companies.find((ac: any) => ac.id == initialAssociateCompany.value)
                : null;
            if (_ac) setLocations(_ac.locations || []);
        }
    }, [parentCompanies]); // eslint-disable-line react-hooks/exhaustive-deps

    const { bulkExportReport, exporting } = useBulkExportReport(
        (data: any) => {
            toast.success(data.message || "Bulk report queued. You will receive a ZIP on your registered email.");
            closeDrawer();
        },
        (error: any) => {
            const msg = (error && error.response && error.response.data && error.response.data.error) || "Failed to queue bulk export.";
            toast.error(msg);
        }
    );

    function closeDrawer() {
        setIsOpen(false);
        setTimeout(onClose, 280);
    }

    function onCompanyChange(e: any) {
        const _company = parentCompanies.find((c: any) => c.id == e.value);
        setCompany(e);
        setAssociateCompanies((_company && _company.associate_companies) || []);
        setAssociateCompany(null);
        setLocation([]);
        setLocations([]);
        setCategory([]);
        setVendor([]);
    }

    function onAssociateCompanyChange(e: any) {
        const _company = parentCompanies.find((c: any) => c.id == (company && company.value));
        const _ac = _company && _company.associate_companies
            ? _company.associate_companies.find((ac: any) => ac.id == e.value)
            : null;
        setAssociateCompany(e);
        setLocations((_ac && _ac.locations) || []);
        setLocation([]);
        setCategory([]);
        setVendor([]);
    }

    function expandMonthRange(start: any, end: any): string[] {
        const result: string[] = [];
        let m = start.month.number;
        let y = start.year;
        const endM = end.month.number;
        const endY = end.year;
        while (y < endY || (y === endY && m <= endM)) {
            result.push(`${monthNames[m - 1]} ${y}`);
            m++;
            if (m > 12) { m = 1; y++; }
        }
        return result;
    }

    function handleSubmit() {
        if (!company || !associateCompany || monthYears.length === 0) {
            toast.error("Please select Company, Associate Company and at least one Month–Year.");
            return;
        }
        const payload: any = {
            company_id: company.value,
            associate_company_id: associateCompany.value,
            month_years: monthYears,
        };
        if (location.length > 0) payload.location_ids = location.map((l: any) => l.value);
        if (category.length > 0) payload.vendor_category_ids = category.map((c: any) => c.value);
        if (vendor.length > 0) payload.vendor_registration_ids = vendor.map((v: any) => v.value);
        bulkExportReport(payload);
    }

    const canSubmit = !!(company && associateCompany && monthYears.length > 0);

    const categoryOptions = initialCategories
        ? initialCategories.map((c: any) => ({ value: c.id, label: c.name }))
        : [];
    const vendorOptions = initialVendors
        ? initialVendors.map((v: any) => ({ value: v.id, label: v.name || v.vendor_name }))
        : [];
    const locationOptions = locations.map((l: any) => ({ value: l.id, label: l.name }));

    return (
        <>
            <div className={`bulk-drawer-backdrop${isOpen ? ' open' : ''}`} onClick={closeDrawer} />
            <div className={`bulk-drawer${isOpen ? ' open' : ''}`} role="dialog" aria-modal="true">
                <div className="la-drawer-head">
                    <div className="la-drawer-title">Download Bulk Report</div>
                    <button type="button" className="la-drawer-close" onClick={closeDrawer} aria-label="Close">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="18" y1="6" x2="6" y2="18" />
                            <line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                    </button>
                </div>

                <div className="la-drawer-body">
                    <div className="la-drawer-field">
                        <label>Company Name</label>
                        <Select
                            options={(parentCompanies || []).map((x: any) => ({ value: x.id, label: x.name }))}
                            value={company}
                            onChange={onCompanyChange}
                            placeholder="Select company"
                            classNamePrefix="drawer-select"
                        />
                    </div>

                    <div className="la-drawer-field">
                        <label>Associate Company</label>
                        <Select
                            options={associateCompanies.map((x: any) => ({ value: x.id, label: x.name }))}
                            value={associateCompany}
                            onChange={onAssociateCompanyChange}
                            placeholder="Select associate company"
                            classNamePrefix="drawer-select"
                            isDisabled={!company}
                        />
                    </div>

                    <div className="la-drawer-field">
                        <label>Audit Month(s) and Year(s)</label>
                        <DatePicker
                            onlyMonthPicker
                            range
                            portal
                            zIndex={11200}
                            value={pickerRange}
                            onChange={(dates: any) => {
                                const arr = Array.isArray(dates) ? dates : (dates ? [dates] : []);
                                setPickerRange(arr);
                                if (arr.length === 2 && arr[0] && arr[1]) {
                                    setMonthYears(expandMonthRange(arr[0], arr[1]));
                                } else {
                                    setMonthYears([]);
                                }
                            }}
                            format="MMM, YYYY"
                            placeholder="Select month range"
                            disabled={!associateCompany}
                            editable={false}
                            inputClass="bulk-month-picker-input"
                        />
                        {monthYears.length > 0 && (
                            <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                                {monthYears.length} month{monthYears.length > 1 ? 's' : ''} selected: {monthYears.join(', ')}
                            </div>
                        )}
                    </div>

                    <div className="la-drawer-field">
                        <label>Locations <span style={{ color: '#9ca3af', fontWeight: 400 }}>(optional)</span></label>
                        <Select
                            options={locationOptions}
                            value={location}
                            onChange={setLocation}
                            placeholder="Select locations"
                            classNamePrefix="drawer-select"
                            isDisabled={!associateCompany}
                            isMulti
                        />
                    </div>

                    {categoryOptions.length > 0 && (
                        <div className="la-drawer-field">
                            <label>Vendor Categories <span style={{ color: '#9ca3af', fontWeight: 400 }}>(optional)</span></label>
                            <Select
                                options={categoryOptions}
                                value={category}
                                onChange={setCategory}
                                placeholder="Select categories"
                                classNamePrefix="drawer-select"
                                isMulti
                            />
                        </div>
                    )}

                    {vendorOptions.length > 0 && (
                        <div className="la-drawer-field">
                            <label>Vendors <span style={{ color: '#9ca3af', fontWeight: 400 }}>(optional)</span></label>
                            <Select
                                options={vendorOptions}
                                value={vendor}
                                onChange={setVendor}
                                placeholder="Select vendors"
                                classNamePrefix="drawer-select"
                                isMulti
                            />
                        </div>
                    )}
                </div>

                <div className="la-drawer-foot">
                    <button
                        type="button"
                        className="la-drawer-download-btn"
                        onClick={handleSubmit}
                        disabled={!canSubmit || exporting}
                    >
                        {exporting ? 'Submitting…' : 'Generate Bulk Report'}
                    </button>
                </div>
            </div>
            {exporting && <PageLoader />}
        </>
    );
}

export default VendorAuditDashboardBulkModal;
